/**
 * Pre-register an upload batch server-side so every queued file has a real
 * document row BEFORE its bytes go up (FS-499).
 *
 * This is the mechanism V1 arrived at, in its current form. V1's own temp rows
 * predate `/v2/files/register-pending` and so had no server id: they were
 * reconciled against real rows by NAME, which needed auto-rename-aware matching,
 * a per-upload cleanup effect, a batch-end backstop and a 30s stale sweeper
 * (`routes/DataCatalog/index.tsx`) — four mechanisms to answer "is this temp row
 * the same file as that real one?". `register-pending` answers it by
 * construction: the id the catalog row carries IS the id the upload later
 * claims, so a `prependDocument` keyed on it dedupes exactly and nothing has to
 * guess. `UnifiedUploadModal` already uploads this way.
 *
 * Registration is best-effort. When it fails the caller still uploads — it just
 * falls back to the legacy behaviour of showing the catalog row once the file
 * has landed, which is strictly what the V2 modal did before.
 */

import { apiService } from '@/service/api'

export interface PendingUploadRequest {
  /** Filename sent to the server; also what the catalog row shows. */
  filename: string
  fileType: string
  fileSize: number
  /** Properties known up front. The upload's own properties win on claim
   *  (`claimPendingUpload` merges `{ ...row.properties, ...properties }`), so a
   *  value that needs an await — a PDF page count, say — can be left out here
   *  and sent with the file. */
  properties?: Record<string, unknown>
  autoRename?: boolean
  replaceDocumentId?: string
}

export interface PendingUploadBatch {
  batchId: string
  /** Server doc id per request, by index. `undefined` where registration
   *  failed or the server returned a short list. */
  docIds: (string | undefined)[]
  /** The name the server actually stored, per request, by index. Registration
   *  resolves collisions (Keep Both, and duplicates WITHIN one batch), so this
   *  can differ from the filename that was sent — `report.pdf` becoming
   *  `report (1).pdf`. The row must show this one, or it displays an existing
   *  document's name until the next refetch. `undefined` where unregistered. */
  filenames: (string | undefined)[]
  /** True only when the server really registered the batch. False means the
   *  caller is on the legacy path: no pre-created rows to claim or discard. */
  registered: boolean
}

/** A batch id that is unique per upload run, falling back where crypto is absent. */
export function newBatchId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
}

/**
 * Register `requests` and return their server doc ids, aligned by index.
 *
 * Never throws: a registration failure must not cost the user their upload.
 */
export async function registerPendingBatch(
  requests: PendingUploadRequest[],
): Promise<PendingUploadBatch> {
  const batchId = newBatchId()
  const empty = (): (string | undefined)[] => requests.map(() => undefined)
  const unregistered: PendingUploadBatch = {
    batchId,
    docIds: empty(),
    filenames: empty(),
    registered: false,
  }
  if (requests.length === 0) return unregistered

  try {
    const resp = await apiService.registerPendingFiles(batchId, requests)
    // The server maps its response off the request array, so index alignment
    // holds — but a short or reordered list must not silently shift every row
    // onto the wrong file, so only accept a response of the expected length.
    const returned = resp?.files
    if (!Array.isArray(returned) || returned.length !== requests.length) {
      console.warn(
        `[FS-499] register-pending returned ${returned?.length ?? 'no'} row(s) for ${requests.length} file(s); falling back to post-upload rows`,
      )
      return unregistered
    }
    return {
      // The server echoes the batch id it filed these rows under — which is
      // what /discard-pending filters on. It is the id we sent, but reading it
      // back means a discard can never target a batch the server didn't make.
      batchId: resp?.batchId ?? batchId,
      docIds: returned.map((r) => r?.docId),
      filenames: returned.map((r) => r?.filename),
      registered: returned.some((r) => !!r?.docId),
    }
  } catch (err) {
    console.warn('[FS-499] register-pending failed; falling back to post-upload rows', err)
    return unregistered
  }
}

/**
 * Soft-delete pending rows whose upload never happened (failed, or abandoned).
 *
 * Without this a pre-registered row sits at "Uploading" forever — the exact
 * zombie the modal's close-blocking guard existed to prevent. Best-effort and
 * idempotent server-side; a no-op when there was nothing registered.
 */
export function discardPendingUploads(batch: PendingUploadBatch, docIds: string[]): void {
  if (!batch.registered || docIds.length === 0) return
  void apiService
    .discardPendingFiles(batch.batchId, docIds)
    .catch((err) => console.error('[FS-499] discard-pending failed', err))
}
